import React, { useEffect } from 'react';
import { observer, inject } from 'mobx-react';

import withUserContext from 'behaviors/withUserContext';
import useCompanyProfileById from 'hooks/useCompanyProfileById';
import { normalizeContext } from 'util/contextUtils';

import EntityProfile from 'components/EntityProfile/EntityProfile';
import convertFeedToV1 from 'util/feed/convertFeedToV1';
import mergeFeedItems from 'util/feed/mergeFeedItems';
import useWalletStore from 'stores/WalletStoreV2';

const CompanyProfileView = ({
  profileStore,
  companyStore,
  uiStore,
  authStore,
  id,
  activeEntity,
}) => {
  useCompanyProfileById({ id, userContext: activeEntity.userContext });
  const walletStore = useWalletStore();
  const companyData = companyStore.companies.get(+id) || {};

  const feed = companyStore.feeds.get(+id);
  const members = companyStore.members.get(+id) || [];
  const events = companyStore.events.get(+id);
  const charities = companyStore.charities.get(+id);
  const groupMember =
    companyData &&
    activeEntity.entityType === 'USER' &&
    (companyData.groupType === 'EXTERNAL_GROUP' ||
      companyData.groupType === 'INTERNAL_GROUP') &&
    profileStore.data.roles.find(
      (role) =>
        role.entity.id === companyData.id &&
        role.entity.entityType === 'COMPANY',
    );
  const isSelf =
    companyData.isSelf ||
    (activeEntity?.entityType === 'COMPANY' &&
      activeEntity?.id?.toString() === companyData?.id?.toString());
  const isMember = members.find(
    (companyMember) =>
      companyMember.id.toString() === activeEntity?.id?.toString() &&
      activeEntity?.entityType === 'USER',
  );

  const showMembers = Boolean(
    members &&
      members?.length &&
      (isMember || companyData.groupType === 'EXTERNAL_GROUP' || isSelf),
  );

  const onFollow = () => {
    companyStore.follow({
      follow: !companyData.isSelfFollowing,
      actorContext: normalizeContext(activeEntity.userContext),
      companyId: id,
    });
  };

  const onLike = ({ feedId }) => {
    const feedItem = feed?.find(
      (feedItem) => feedItem?.activityPurchase?.id === feedId,
    );
    const feedItemIndex = feed?.findIndex(
      (feedItem) => feedItem?.activityPurchase?.id === feedId,
    );
    if (feedItem) {
      companyStore.like({
        like: !feedItem.currentEntityLiked,
        id: feedId,
        purchaseId: feedItem.activityPurchase?.id,
        companyId: id,
        userContext: normalizeContext(profileStore.activeEntity.userContext),
      });
    }
    feed[feedItemIndex].likeCount = !feedItem.currentEntityLiked
      ? feedItem.likeCount + 1
      : feedItem.likeCount - 1;
    feed[feedItemIndex].currentEntityLiked = !feedItem.currentEntityLiked;
  };

  const feedLoadMore = () => {
    companyStore.loadMore({ id });
  };

  const onRearrange = async (newCharities) => {
    if (id) {
      companyStore.setCharities({ followerId: id, charities: newCharities });
    }

    await Promise.all(
      newCharities.map(async (charity, index) => {
        companyStore.updateFollow({
          charityId: charity.id,
          priority: index,
        });
      }),
    );
  };

  const showCheckoutModal = () => {
    uiStore.openModal('CHECKOUT', {
      activeEntity,
      walletStore,
      giftToken: uiStore.savedUiState?.userToken?.token,
      hideAmount: uiStore.savedUiState?.userToken?.gift?.hideAmount,
      giftAmount: uiStore.savedUiState?.userToken?.gift?.amount,
      email: uiStore.savedUiState.userToken?.email || profileStore?.data?.email,
      initiatorId: uiStore.savedUiState.userToken?.gift?.initiatorCompany?.id,
      checkoutType: 'gift',
      userProfileData: {
        firstName: companyData?.name,
        avatarUrls: companyData?.avatar,
      },
      companyIds: [id],
      eventId: null,
      firstName: activeEntity?.firstName,
      lastName: activeEntity?.lastName,
      zip: activeEntity?.zip,
      amount: 500,
      giftType: 'USER_GIFT',
      onCheckoutSuccess: async (state) => {
        uiStore.showNotification({
          body: `${state.amount === 0 ? 'Invites' : 'Gifts'} Sent`,
          type: 'SUCCESS',
        });
        profileStore.getProfile();
      },
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (isSelf) {
      return;
    }

    if (Object.keys(companyData).length > 0) {
      if (params.get('gift') === '1') {
        showCheckoutModal();
      }
    }
  }, [companyData]);

  return (
    <EntityProfile
      isCompanyProfilePage
      featuredProjects={charities}
      loading={companyData?.loading}
      activeEntity={activeEntity}
      onCheckoutSuccess={() => {
        companyStore.getCompanyFeed({
          id: +id,
          userContext: normalizeContext(activeEntity.userContext),
        });
      }}
      entityData={{
        ...companyData,
        bio: companyData.description,
      }}
      isAuthenticated={authStore.isAuthenticated}
      onFollow={onFollow}
      onLike={onLike}
      entityId={id}
      onRearrange={onRearrange}
      isSelf={isSelf}
      groupMember={groupMember}
      uiStore={uiStore}
      events={events?.filter((event) => event.eventState === 'ACTIVE')}
      eventsLoading={companyStore.eventsLoading}
      feedLoading={companyStore.feedLoading}
      feedLoadMore={feedLoadMore}
      feedLoadingMore={companyStore.feedLoadingMore}
      feed={mergeFeedItems(feed)?.map(convertFeedToV1)}
      showCheckoutModal={showCheckoutModal}
      companyMembers={members.map((member) => ({
        ...member,
        entityType: 'USER',
      }))}
      entityType="COMPANY"
      isVerified
      hideFollowerCount
      showMembers={showMembers}
      showEditButton={companyData.isSelf || profileStore.activeEntity.sysAdmin}
      store={{
        ...companyStore,
        follow: ({ follow, targetContext, targetId }) => {
          companyStore.followEntity({
            follow,
            companyId: id,
            targetContext: {
              ...targetContext,
              userId: targetId,
            },
            actorContext: {
              ...activeEntity.userContext,
            },
          });
        },
      }}
    />
  );
};

export default withUserContext(
  inject(
    'profileStore',
    'companyStore',
    'authStore',
    'uiStore',
  )(observer(CompanyProfileView)),
);
